import React, { useState, useMemo } from 'react';
import { Card, Button, Input, Badge } from '../../components/ui';
import {
  Building2,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Printer,
  TrendingUp,
  Plus,
  Trash2,
  Layers,
  FileText,
  Percent,
  Calendar,
  HelpCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface ReserveItem {
  id: string;
  name: string;
  estimatedCost: number;
  yearNeeded: number;
}

interface ExpenseCategory {
  id: string;
  name: string;
  annualBudget: number;
  note?: string;
}

const DEFAULT_EXPENSES: ExpenseCategory[] = [
  { id: 'landscaping', name: 'Landscaping & Grounds Keeping', annualBudget: 36000 },
  { id: 'utilities', name: 'Common Utilities & Irrigation Water', annualBudget: 24000 },
  { id: 'amenities', name: 'Pool, Gate & Clubhouse Maintenance', annualBudget: 18000 },
  { id: 'management', name: 'Management Fees & Accounting', annualBudget: 15000 },
  { id: 'insurance', name: 'Property & Director Liability Insurance', annualBudget: 22000 },
  { id: 'legal', name: 'Legal, Filings & Administrative', annualBudget: 5000 },
];

const DEFAULT_RESERVES: ReserveItem[] = [
  { id: 'r1', name: 'Clubhouse Roof Replacement', estimatedCost: 45000, yearNeeded: 3 },
  { id: 'r2', name: 'Asphalt Resurfacing & Sealcoating', estimatedCost: 35000, yearNeeded: 5 },
  { id: 'r3', name: 'Pool Resurfacing & Pump Equipment', estimatedCost: 20000, yearNeeded: 7 },
  { id: 'r4', name: 'Perimeter Fence & Gate Automation', estimatedCost: 15000, yearNeeded: 8 },
];

export function HoaBudgetTrackerTool() {
  const [activeTab, setActiveTab] = useState<'operating' | 'reserves' | 'summary'>('operating');

  // Community Metadata
  const [communityName, setCommunityName] = useState('Pinecrest Homeowners Association');
  const [fiscalYear, setFiscalYear] = useState('2026');
  const [unitCount, setUnitCount] = useState<number>(120);
  const [monthlyAssessment, setMonthlyAssessment] = useState<number>(245);
  const [collectionRate, setCollectionRate] = useState<number>(96); // 96% collection / 4% delinquency
  const [miscAnnualRevenue, setMiscAnnualRevenue] = useState<number>(4500); // Late fees, transfer fees, rentals

  // Operating Expenses
  const [expenses, setExpenses] = useState<ExpenseCategory[]>(DEFAULT_EXPENSES);
  const [newExpName, setNewExpName] = useState('');
  const [newExpAmount, setNewExpAmount] = useState<number>(0);

  // Reserve Tracking
  const [currentReserveBalance, setCurrentReserveBalance] = useState<number>(95000);
  const [monthlyReserveTransfer, setMonthlyReserveTransfer] = useState<number>(2500);
  const [reserveInterestRate, setReserveInterestRate] = useState<number>(1.5); // 1.5% HYSA
  const [reserveProjects, setReserveProjects] = useState<ReserveItem[]>(DEFAULT_RESERVES);
  const [newProjName, setNewProjName] = useState('');
  const [newProjCost, setNewProjCost] = useState<number>(0);
  const [newProjYear, setNewProjYear] = useState<number>(3);

  // Calculations: Operating
  const grossAnnualDues = unitCount * monthlyAssessment * 12;
  const netDuesCollected = grossAnnualDues * (collectionRate / 100);
  const totalAnnualRevenue = netDuesCollected + miscAnnualRevenue;

  const totalOperatingExpenses = useMemo(() => {
    return expenses.reduce((sum, e) => sum + (e.annualBudget || 0), 0);
  }, [expenses]);

  const annualReserveContribution = monthlyReserveTransfer * 12;
  const totalAnnualOutflows = totalOperatingExpenses + annualReserveContribution;
  const netAnnualSurplus = totalAnnualRevenue - totalAnnualOutflows;
  const monthlyCostPerDoor = unitCount > 0 ? (totalAnnualOutflows / unitCount / 12) : 0;

  // Calculations: 10-Year Reserve Projection
  const reserveSchedule = useMemo(() => {
    const rows = [];
    let balance = currentReserveBalance;

    for (let yr = 1; yr <= 10; yr++) {
      const startBalance = balance;
      const interest = startBalance * (reserveInterestRate / 100);
      const contribution = annualReserveContribution;
      const projectsInYear = reserveProjects.filter(p => p.yearNeeded === yr);
      const projectOutlays = projectsInYear.reduce((sum, p) => sum + p.estimatedCost, 0);

      const endBalance = startBalance + contribution + interest - projectOutlays;
      balance = endBalance;

      rows.push({
        year: yr,
        calendarYear: parseInt(fiscalYear) + yr - 1,
        startBalance,
        contribution,
        interest,
        projectOutlays,
        projects: projectsInYear,
        endBalance
      });
    }
    return rows;
  }, [currentReserveBalance, annualReserveContribution, reserveInterestRate, reserveProjects, fiscalYear]);

  const total10YearProjects = useMemo(() => {
    return reserveProjects.reduce((sum, p) => sum + p.estimatedCost, 0);
  }, [reserveProjects]);

  const percentFunded = total10YearProjects > 0
    ? Math.min(100, Math.round((currentReserveBalance / total10YearProjects) * 100))
    : 100;

  const handleAddExpense = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExpName.trim() || newExpAmount <= 0) return;
    setExpenses(prev => [
      ...prev,
      { id: `exp_${Date.now()}`, name: newExpName.trim(), annualBudget: newExpAmount }
    ]);
    setNewExpName('');
    setNewExpAmount(0);
  };

  const handleRemoveExpense = (id: string) => {
    setExpenses(prev => prev.filter(e => e.id !== id));
  };

  const handleAddProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjName.trim() || newProjCost <= 0) return;
    setReserveProjects(prev => [
      ...prev,
      { id: `proj_${Date.now()}`, name: newProjName.trim(), estimatedCost: newProjCost, yearNeeded: newProjYear }
    ]);
    setNewProjName('');
    setNewProjCost(0);
  };

  const handleRemoveProject = (id: string) => {
    setReserveProjects(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="w-full text-left space-y-8 font-sans">
      {/* Print Controls & Sub-nav */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b-4 border-black dark:border-white pb-4 print:hidden">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('operating')}
            className={cn(
              'px-4 py-2 text-xs font-black uppercase tracking-wider border-2 border-black dark:border-white transition-all cursor-pointer',
              activeTab === 'operating'
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-[4px_4px_0px_#FFD600]'
                : 'bg-white dark:bg-black text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
            )}
          >
            1. Operating Budget
          </button>
          <button
            onClick={() => setActiveTab('reserves')}
            className={cn(
              'px-4 py-2 text-xs font-black uppercase tracking-wider border-2 border-black dark:border-white transition-all cursor-pointer',
              activeTab === 'reserves'
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-[4px_4px_0px_#0044FF]'
                : 'bg-white dark:bg-black text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
            )}
          >
            2. Reserve Tracking (10-Yr)
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={cn(
              'px-4 py-2 text-xs font-black uppercase tracking-wider border-2 border-black dark:border-white transition-all cursor-pointer',
              activeTab === 'summary'
                ? 'bg-[#0044FF] text-white shadow-[4px_4px_0px_#FFD600]'
                : 'bg-white dark:bg-black text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
            )}
          >
            3. Board Report & Disclosures
          </button>
        </div>

        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-5 py-2.5 bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-wider border-2 border-black dark:border-white shadow-[4px_4px_0px_#FFD600] hover:bg-[#0044FF] hover:text-white transition-all cursor-pointer"
        >
          <Printer className="w-4 h-4" /> Print HOA Disclosure
        </button>
      </div>

      {/* Top Level Vital Gauges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
          <span className="text-[10px] uppercase font-black tracking-widest text-black/50 dark:text-white/50 block mb-1">
            Total Annual Rev
          </span>
          <div className="text-2xl font-black font-mono text-black dark:text-white">
            ${Math.round(totalAnnualRevenue).toLocaleString()}
          </div>
          <span className="text-[9px] font-bold text-black/40 dark:text-white/40">
            {collectionRate}% collection rate
          </span>
        </div>

        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
          <span className="text-[10px] uppercase font-black tracking-widest text-black/50 dark:text-white/50 block mb-1">
            Operating Outflows
          </span>
          <div className="text-2xl font-black font-mono text-black dark:text-white">
            ${Math.round(totalAnnualOutflows).toLocaleString()}
          </div>
          <span className="text-[9px] font-bold text-black/40 dark:text-white/40">
            Incl. ${annualReserveContribution.toLocaleString()} reserves
          </span>
        </div>

        <div className={cn(
          'p-4 border-4 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]',
          netAnnualSurplus >= 0 ? 'bg-emerald-50 dark:bg-emerald-950/20' : 'bg-red-50 dark:bg-red-950/20'
        )}>
          <span className="text-[10px] uppercase font-black tracking-widest block mb-1 text-black/60 dark:text-white/60">
            Operating Net
          </span>
          <div className={cn(
            'text-2xl font-black font-mono',
            netAnnualSurplus >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
          )}>
            {netAnnualSurplus >= 0 ? '+' : ''}${Math.round(netAnnualSurplus).toLocaleString()}
          </div>
          <span className="text-[9px] font-bold">
            {netAnnualSurplus >= 0 ? 'Balanced surplus' : 'Assessment deficit!'}
          </span>
        </div>

        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
          <span className="text-[10px] uppercase font-black tracking-widest text-black/50 dark:text-white/50 block mb-1">
            Reserve Health
          </span>
          <div className="text-2xl font-black font-mono text-[#0044FF]">
            {percentFunded}%
          </div>
          <span className="text-[9px] font-bold text-black/40 dark:text-white/40">
            ${currentReserveBalance.toLocaleString()} in reserves
          </span>
        </div>
      </div>

      {/* TAB 1: OPERATING BUDGET */}
      {activeTab === 'operating' && (
        <div className="grid lg:grid-cols-12 gap-8 print:hidden">
          {/* Revenue Controls */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="rounded-none border-4 border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] bg-white dark:bg-black space-y-4">
              <h3 className="text-lg font-black uppercase tracking-tight font-display border-b-2 border-black dark:border-white pb-2 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0044FF]" /> Assessment & Revenue
              </h3>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                  Community / Association Name
                </label>
                <input
                  type="text"
                  value={communityName}
                  onChange={e => setCommunityName(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                    Total Units / Doors
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={unitCount}
                    onChange={e => setUnitCount(Math.max(1, parseInt(e.target.value) || 0))}
                    className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                    Monthly Dues / Unit ($)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={monthlyAssessment}
                    onChange={e => setMonthlyAssessment(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                    Collection Rate (%)
                  </label>
                  <input
                    type="number"
                    min={50}
                    max={100}
                    value={collectionRate}
                    onChange={e => setCollectionRate(Math.min(100, Math.max(50, parseFloat(e.target.value) || 0)))}
                    className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                    Misc. Revenue / Year ($)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={miscAnnualRevenue}
                    onChange={e => setMiscAnnualRevenue(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                  />
                </div>
              </div>

              <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 space-y-1 text-xs font-mono">
                <div className="flex justify-between">
                  <span>Gross Assessment Potential:</span>
                  <span className="font-bold">${grossAnnualDues.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-black/60 dark:text-white/60">
                  <span>Estimated Delinquency ({100 - collectionRate}%):</span>
                  <span>-${Math.round(grossAnnualDues - netDuesCollected).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-black dark:text-white pt-1 border-t">
                  <span>Net Annual Operating Inflow:</span>
                  <span className="text-[#0044FF]">${Math.round(totalAnnualRevenue).toLocaleString()}</span>
                </div>
              </div>
            </Card>

            {/* Monthly Reserve Allocation */}
            <Card className="rounded-none border-4 border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] bg-white dark:bg-black space-y-3">
              <h3 className="text-md font-black uppercase tracking-tight font-display flex items-center justify-between">
                <span>Monthly Reserve Contribution</span>
                <span className="text-xs font-mono text-[#0044FF]">${monthlyReserveTransfer}/mo</span>
              </h3>
              <p className="text-xs text-black/60 dark:text-white/60">
                Amount automatically transferred from operating checking to the reserve fund each month.
              </p>
              <input
                type="range"
                min={500}
                max={15000}
                step={250}
                value={monthlyReserveTransfer}
                onChange={e => setMonthlyReserveTransfer(parseInt(e.target.value))}
                className="w-full accent-[#0044FF] cursor-pointer"
              />
              <div className="flex justify-between text-[10px] font-mono font-bold text-black/40 dark:text-white/40">
                <span>$500/mo ($6k/yr)</span>
                <span>Annual: ${annualReserveContribution.toLocaleString()}</span>
                <span>$15k/mo ($180k/yr)</span>
              </div>
            </Card>
          </div>

          {/* Operating Expense Items */}
          <div className="lg:col-span-7 space-y-6">
            <Card className="rounded-none border-4 border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] bg-white dark:bg-black space-y-4">
              <div className="flex justify-between items-center border-b-2 border-black dark:border-white pb-3">
                <h3 className="text-lg font-black uppercase tracking-tight font-display flex items-center gap-2">
                  <Layers className="w-5 h-5 text-emerald-600" /> Annual Operating Expenses
                </h3>
                <span className="text-xs font-mono font-black px-2 py-1 bg-black text-white dark:bg-white dark:text-black">
                  Total: ${totalOperatingExpenses.toLocaleString()}/yr
                </span>
              </div>

              {/* Expense List */}
              <div className="space-y-3">
                {expenses.map((exp) => {
                  const percentOfBudget = totalOperatingExpenses > 0
                    ? Math.round((exp.annualBudget / totalOperatingExpenses) * 100)
                    : 0;
                  const perDoorMonthly = unitCount > 0 ? (exp.annualBudget / unitCount / 12) : 0;

                  return (
                    <div
                      key={exp.id}
                      className="p-3 border-2 border-black dark:border-white flex items-center justify-between gap-4 bg-white dark:bg-[#0A0A0A]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-black dark:text-white truncate">{exp.name}</div>
                        <div className="text-[10px] font-mono text-black/50 dark:text-white/50 flex gap-3 mt-0.5">
                          <span>{percentOfBudget}% of expenses</span>
                          <span>${perDoorMonthly.toFixed(2)}/door/mo</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-sm font-black font-mono">
                          ${exp.annualBudget.toLocaleString()}
                        </span>
                        <button
                          onClick={() => handleRemoveExpense(exp.id)}
                          className="p-1 hover:text-red-600 text-black/30 dark:text-white/30 transition-colors cursor-pointer"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add Expense Form */}
              <form onSubmit={handleAddExpense} className="pt-4 border-t-2 border-black dark:border-white space-y-3">
                <span className="text-[10px] font-black uppercase tracking-widest block text-black/60 dark:text-white/60">
                  + Add Custom Expense Category
                </span>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    placeholder="Expense line (e.g., Security Patrol, Pest Control)"
                    value={newExpName}
                    onChange={e => setNewExpName(e.target.value)}
                    className="flex-1 px-3 py-2 border-2 border-black dark:border-white text-xs font-bold bg-white dark:bg-[#0A0A0A]"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold">$</span>
                    <input
                      type="number"
                      placeholder="Annual $"
                      value={newExpAmount || ''}
                      onChange={e => setNewExpAmount(parseFloat(e.target.value) || 0)}
                      className="w-28 px-3 py-2 border-2 border-black dark:border-white text-xs font-bold bg-white dark:bg-[#0A0A0A]"
                    />
                    <Button type="submit" variant="primary" size="sm" className="rounded-none font-black uppercase text-xs">
                      Add
                    </Button>
                  </div>
                </div>
              </form>
            </Card>

            {/* Per-Door Assessment Analysis */}
            <div className="p-4 border-2 border-black dark:border-white bg-[#FFD600]/10 border-dashed space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-black dark:text-white">
                Monthly Cost Per Door Breakdown
              </h4>
              <div className="grid grid-cols-3 gap-2 text-center text-xs font-mono">
                <div className="p-2 border border-black/20 dark:border-white/20 bg-white dark:bg-black">
                  <div className="text-[9px] uppercase font-bold text-black/50 dark:text-white/50">Operating Ops</div>
                  <div className="font-bold text-sm mt-0.5">
                    ${(unitCount > 0 ? totalOperatingExpenses / unitCount / 12 : 0).toFixed(2)}
                  </div>
                </div>
                <div className="p-2 border border-black/20 dark:border-white/20 bg-white dark:bg-black">
                  <div className="text-[9px] uppercase font-bold text-black/50 dark:text-white/50">Reserve Dues</div>
                  <div className="font-bold text-sm mt-0.5">
                    ${(unitCount > 0 ? annualReserveContribution / unitCount / 12 : 0).toFixed(2)}
                  </div>
                </div>
                <div className="p-2 border border-black/20 dark:border-white/20 bg-white dark:bg-black">
                  <div className="text-[9px] uppercase font-bold text-black/50 dark:text-white/50">Actual Billed</div>
                  <div className="font-bold text-sm mt-0.5 text-[#0044FF]">
                    ${monthlyAssessment.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: 10-YEAR RESERVE FUND TRACKER */}
      {activeTab === 'reserves' && (
        <div className="space-y-6 print:hidden">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6 border-4 border-black dark:border-white rounded-none bg-white dark:bg-black shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] space-y-4">
              <h3 className="text-sm font-black uppercase tracking-wider border-b-2 border-black dark:border-white pb-2">
                Current Reserve Holdings
              </h3>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                  Starting Balance ($)
                </label>
                <input
                  type="number"
                  min={0}
                  value={currentReserveBalance}
                  onChange={e => setCurrentReserveBalance(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                  HYSA / Treasury Yield (%)
                </label>
                <input
                  type="number"
                  step={0.1}
                  min={0}
                  value={reserveInterestRate}
                  onChange={e => setReserveInterestRate(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-sm"
                />
              </div>
              <div className="text-xs text-black/60 dark:text-white/60">
                Annual transfer from operating: <strong>${annualReserveContribution.toLocaleString()}</strong>
              </div>
            </Card>

            <Card className="md:col-span-2 p-6 border-4 border-black dark:border-white rounded-none bg-white dark:bg-black shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] space-y-4">
              <div className="flex justify-between items-center border-b-2 border-black dark:border-white pb-2">
                <h3 className="text-sm font-black uppercase tracking-wider">
                  Major Component Replacement Projects
                </h3>
                <span className="text-xs font-mono font-bold">10-Yr Liability: ${total10YearProjects.toLocaleString()}</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 max-h-56 overflow-y-auto pr-1">
                {reserveProjects.map((p) => (
                  <div key={p.id} className="p-3 border-2 border-black dark:border-white bg-[#F5F5F0] dark:bg-[#111111] flex justify-between items-start">
                    <div>
                      <div className="font-bold text-xs">{p.name}</div>
                      <div className="text-[10px] font-mono text-black/50 dark:text-white/50 mt-1">
                        Year {p.yearNeeded} ({parseInt(fiscalYear) + p.yearNeeded - 1})
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-black text-xs">${p.estimatedCost.toLocaleString()}</span>
                      <button
                        onClick={() => handleRemoveProject(p.id)}
                        className="text-black/30 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Project Form */}
              <form onSubmit={handleAddProject} className="pt-2 border-t flex flex-wrap gap-2 items-center text-xs">
                <input
                  type="text"
                  placeholder="Component (e.g. Elevator Modernization)"
                  value={newProjName}
                  onChange={e => setNewProjName(e.target.value)}
                  className="flex-1 min-w-[160px] px-2 py-1.5 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold"
                />
                <input
                  type="number"
                  placeholder="Cost ($)"
                  value={newProjCost || ''}
                  onChange={e => setNewProjCost(parseFloat(e.target.value) || 0)}
                  className="w-24 px-2 py-1.5 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold"
                />
                <select
                  value={newProjYear}
                  onChange={e => setNewProjYear(parseInt(e.target.value))}
                  className="px-2 py-1.5 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(yr => (
                    <option key={yr} value={yr}>Yr {yr} ({parseInt(fiscalYear) + yr - 1})</option>
                  ))}
                </select>
                <Button type="submit" variant="primary" size="sm" className="rounded-none font-bold uppercase text-[10px]">
                  Add Project
                </Button>
              </form>
            </Card>
          </div>

          {/* 10-Year Trajectory Table */}
          <Card className="p-6 border-4 border-black dark:border-white rounded-none bg-white dark:bg-black shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] space-y-4">
            <h3 className="text-md font-black uppercase tracking-tight font-display">
              10-Year Reserve Fund Cash-Flow Trajectory
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs font-mono">
                <thead>
                  <tr className="border-b-4 border-black dark:border-white bg-[#F5F5F0] dark:bg-[#111111]">
                    <th className="p-2.5">Year</th>
                    <th className="p-2.5">Starting Balance</th>
                    <th className="p-2.5">+ Contributions</th>
                    <th className="p-2.5">+ Interest</th>
                    <th className="p-2.5 text-red-600">- Capital Projects</th>
                    <th className="p-2.5 text-right font-bold">Ending Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {reserveSchedule.map((row) => {
                    const isDeficit = row.endBalance < 0;
                    return (
                      <tr
                        key={row.year}
                        className={cn(
                          'border-b border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5',
                          isDeficit && 'bg-red-500/10'
                        )}
                      >
                        <td className="p-2.5 font-bold">
                          Yr {row.year} <span className="text-[10px] text-black/50 dark:text-white/50">({row.calendarYear})</span>
                        </td>
                        <td className="p-2.5">${Math.round(row.startBalance).toLocaleString()}</td>
                        <td className="p-2.5 text-emerald-600">+${Math.round(row.contribution).toLocaleString()}</td>
                        <td className="p-2.5 text-emerald-600">+${Math.round(row.interest).toLocaleString()}</td>
                        <td className="p-2.5">
                          {row.projectOutlays > 0 ? (
                            <span className="text-red-600 font-bold">
                              -${row.projectOutlays.toLocaleString()}{' '}
                              <span className="text-[10px] text-black/50 dark:text-white/50 block">
                                ({row.projects.map(p => p.name).join(', ')})
                              </span>
                            </span>
                          ) : (
                            <span className="text-black/30 dark:text-white/30">—</span>
                          )}
                        </td>
                        <td className={cn(
                          'p-2.5 text-right font-bold text-sm',
                          row.endBalance >= 0 ? 'text-black dark:text-white' : 'text-red-600'
                        )}>
                          ${Math.round(row.endBalance).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 3: PRINTABLE BOARD SUMMARY & HOMEOWNER DISCLOSURE */}
      <div className={cn(
          'p-8 bg-white text-black border-4 border-black font-sans mx-auto max-w-4xl shadow-[8px_8px_0px_#000000]',
          activeTab !== 'summary' ? 'hidden print:block' : 'block'
        )}>
          {/* Header */}
          <div className="border-b-4 border-black pb-4 mb-6 flex justify-between items-start">
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-black/60 block">
                Annual Financial Assessment Notice
              </span>
              <h2 className="text-2xl font-black uppercase tracking-tight">{communityName}</h2>
              <p className="text-xs text-black/60 mt-0.5">
                Fiscal Year {fiscalYear} Operating Budget & 10-Year Reserve Component Schedule
              </p>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-mono uppercase bg-black text-white px-2 py-1 font-bold">
                {unitCount} Total Units
              </span>
              <div className="text-sm font-black font-mono mt-1 text-[#0044FF]">
                ${monthlyAssessment.toFixed(2)}/mo per door
              </div>
            </div>
          </div>

          {/* High-Level Overview Grid */}
          <div className="grid grid-cols-3 gap-4 border-2 border-black p-4 mb-6 bg-[#F5F5F0]">
            <div>
              <div className="text-[10px] font-bold uppercase text-black/60">Annual Gross Revenues</div>
              <div className="text-lg font-black font-mono">${Math.round(totalAnnualRevenue).toLocaleString()}</div>
              <div className="text-[9px] text-black/50">Incl. ${miscAnnualRevenue.toLocaleString()} misc</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-black/60">Operating Expenditures</div>
              <div className="text-lg font-black font-mono">${Math.round(totalOperatingExpenses).toLocaleString()}</div>
              <div className="text-[9px] text-black/50">${(totalOperatingExpenses / unitCount / 12).toFixed(2)}/door/mo</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-black/60">Reserve Contributions</div>
              <div className="text-lg font-black font-mono">${Math.round(annualReserveContribution).toLocaleString()}</div>
              <div className="text-[9px] text-black/50">${(annualReserveContribution / unitCount / 12).toFixed(2)}/door/mo</div>
            </div>
          </div>

          {/* Line Item Operating Budget */}
          <div className="mb-6">
            <h3 className="text-xs font-black uppercase tracking-widest border-b-2 border-black pb-1 mb-2">
              Itemized Operating Expenditures
            </h3>
            <table className="w-full text-left text-xs font-mono border-collapse">
              <thead>
                <tr className="border-b border-black text-black/60 text-[10px]">
                  <th className="py-1">Line Item Category</th>
                  <th className="py-1 text-right">Annual Budget</th>
                  <th className="py-1 text-right">Per Door/Mo</th>
                  <th className="py-1 text-right">% of OpEx</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => (
                  <tr key={exp.id} className="border-b border-black/10">
                    <td className="py-1.5 font-bold font-sans">{exp.name}</td>
                    <td className="py-1.5 text-right">${exp.annualBudget.toLocaleString()}</td>
                    <td className="py-1.5 text-right">${(exp.annualBudget / unitCount / 12).toFixed(2)}</td>
                    <td className="py-1.5 text-right">
                      {totalOperatingExpenses > 0 ? Math.round((exp.annualBudget / totalOperatingExpenses) * 100) : 0}%
                    </td>
                  </tr>
                ))}
                <tr className="font-bold border-t-2 border-black text-sm">
                  <td className="py-2 font-sans uppercase">Total Operating Line Items</td>
                  <td className="py-2 text-right">${totalOperatingExpenses.toLocaleString()}</td>
                  <td className="py-2 text-right">${(totalOperatingExpenses / unitCount / 12).toFixed(2)}</td>
                  <td className="py-2 text-right">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Reserve Study Disclosure */}
          <div className="mb-6">
            <h3 className="text-xs font-black uppercase tracking-widest border-b-2 border-black pb-1 mb-2">
              Reserve Fund Status & Upcoming Capital Replacements
            </h3>
            <div className="grid grid-cols-2 gap-4 text-xs font-mono mb-3">
              <div>
                <span>Current Reserve Balance:</span> <strong>${currentReserveBalance.toLocaleString()}</strong>
              </div>
              <div className="text-right">
                <span>10-Year Capital Liability:</span> <strong>${total10YearProjects.toLocaleString()}</strong>
              </div>
            </div>

            <div className="space-y-1 text-xs">
              {reserveProjects.map((p) => (
                <div key={p.id} className="flex justify-between py-1 border-b border-black/10">
                  <span>
                    Year {p.yearNeeded} ({parseInt(fiscalYear) + p.yearNeeded - 1}) — <strong>{p.name}</strong>
                  </span>
                  <span className="font-mono">${p.estimatedCost.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Board Signature Sign-Off */}
          <div className="pt-6 border-t-2 border-black grid grid-cols-2 gap-8 text-xs">
            <div>
              <div className="border-b border-black pb-8 mb-1"></div>
              <span className="font-bold uppercase tracking-wider text-[10px]">HOA Board President Signature & Date</span>
            </div>
            <div>
              <div className="border-b border-black pb-8 mb-1"></div>
              <span className="font-bold uppercase tracking-wider text-[10px]">HOA Board Treasurer Signature & Date</span>
            </div>
          </div>

          <div className="mt-8 text-center text-[9px] text-black/40 font-mono">
            Prepared with OrgSets HOA Budgeting & Reserve Tracking Suite · orgsets.com
          </div>
        </div>
    </div>
  );
}
